/*  End‑of‑Week Report – Detail Page
    • Consistent UI with other report pages
    • Exports: default (React page) + getEOWRPdfContentDefinition + preparePerformanceData
*/
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, query, where, Timestamp,
  orderBy, getDocs, limit,
} from 'firebase/firestore';
import { format, parseISO, startOfDay, endOfDay, addDays, isValid } from 'date-fns';
import {
  FiCalendar, FiUser, FiClipboard, FiThumbsUp,
  FiThumbsDown, FiKey, FiTarget, FiDownload, FiInfo,
} from 'react-icons/fi';
import pdfMake   from 'pdfmake/build/pdfmake';
import pdfFonts  from 'pdfmake/build/vfs_fonts';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

import { AuthContext } from '../contexts/AuthContext';
import { db }           from '../utils/firebaseConfig';
import { pdfStyles }    from '../utils/pdfStyles';
import { parseDate, getBase64ImageFromURL } from '../utils/pdfUtils';
import { ERRORS }       from '../utils/constants';

pdfMake.vfs = pdfFonts.vfs;

/* ------------------------------------------------ PDF Constants ---- */
const PDF_CONFIG = {
  BANNER_WIDTH: 515,
  PAGE_MARGINS: [40, 20, 40, 40],
  MAX_DATES_SHOWN: 7,
  MAX_DATE_COLUMNS: 7,
  TABLE_PADDING: { left: 8, right: 8, top: 4, bottom: 4 },
  COLORS: {
    primary: '#2563eb',      // Blue-600
    secondary: '#3b82f6',    // Blue-500
    accent: '#93c5fd',       // Blue-300
    success: '#16a34a',      // Green-600
    warning: '#d97706',      // Amber-600
    danger: '#dc2626',       // Red-600
    tableHeader: '#1e40af',  // Blue-800
    tableAlt: '#f8fafc',     // Slate-50
    text: '#1e293b',         // Slate-800
    lightText: '#64748b',    // Slate-500
    border: '#cbd5e1',       // Slate-300
    headerBg: '#dbeafe',     // Blue-100
    dangerBg: '#fee2e2',     // Red-100
    successBg: '#dcfce7',    // Green-100
  }
};

/* Enhanced PDF Styles */
const enhancedPdfStyles = {
  ...pdfStyles,
  header: {
    fontSize: 24,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
    margin: [0, 0, 0, 8],
  },
  subheader: {
    fontSize: 11,
    color: PDF_CONFIG.COLORS.text,
    margin: [0, 0, 0, 4],
  },
  sectionHeader: {
    fontSize: 14,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
    margin: [0, 15, 0, 8],
  },
  tableHeader: {
    fontSize: 9,
    bold: true,
    color: 'white',
    fillColor: PDF_CONFIG.COLORS.tableHeader,
    margin: [4, 2],
  },
  tableHeaderSmall: {
    fontSize: 8,
    color: 'white',
    opacity: 0.9,
    margin: [4, 0],
  },
  tableCell: {
    fontSize: 9,
    color: PDF_CONFIG.COLORS.text,
    margin: [4, 2],
  },
  tableCellOne: {
    fontSize: 9,
    bold: true,
    color: PDF_CONFIG.COLORS.danger,
    fillColor: PDF_CONFIG.COLORS.dangerBg,
    margin: [4, 2],
  },
  tableCellFive: {
    fontSize: 9,
    bold: true,
    color: PDF_CONFIG.COLORS.success,
    fillColor: PDF_CONFIG.COLORS.successBg,
    margin: [4, 2],
  },
  footerText: {
    fontSize: 8,
    color: PDF_CONFIG.COLORS.lightText,
  },
  bannerText: {
    fontSize: 26,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
  },
  content: {
    fontSize: 10,
    color: PDF_CONFIG.COLORS.text,
    lineHeight: 1.5,
    margin: [0, 0, 0, 10],
  },
  infoBox: {
    fontSize: 9,
    color: PDF_CONFIG.COLORS.text,
    fillColor: PDF_CONFIG.COLORS.headerBg,
    margin: [8, 8],
  },
  footnote: {
    fontSize: 8,
    italics: true,
    color: PDF_CONFIG.COLORS.lightText,
  },
  reportNumber: {
    fontSize: 10,
    color: PDF_CONFIG.COLORS.lightText,
    alignment: 'right',
  },
};

/* ------------------------------------------------ Utility Functions ---- */
const formatPdfDate = (date, formatStr = 'MMMM d, yyyy') => {
  if (!date) return 'N/A';
  const parsed = parseDate(date);
  return isValid(parsed) ? format(parsed, formatStr) : 'N/A';
};

const calculateAverage = (ratings) => {
  const validRatings = Object.values(ratings).filter(r => r !== null).map(Number);
  if (!validRatings.length) return 'N/A';
  const avg = validRatings.reduce((a, b) => a + b, 0) / validRatings.length;
  return avg.toFixed(2);
};

const getRatingStyle = (rating) => {
  if (rating === null || rating === 'N/A') return 'tableCell';
  const numRating = Number(rating);
  if (numRating === 1) return 'tableCellOne';
  if (numRating === 5) return 'tableCellFive';
  return 'tableCell';
};

/* ------------------------------------------------ PDF builder ---- */
export const getEOWRPdfContentDefinition = async (report, perf) => {
  // Validate input
  if (!report || !report.weekEndingDate) {
    throw new Error('Invalid report data for PDF generation');
  }

  const { tableData = [], sortedDorDates = [], explanations = [] } = perf || {};
  const content = [];

  /* Banner with fallback */
  try {
    const banner = await getBase64ImageFromURL('/banner.png');
    content.push({ 
      image: banner, 
      width: PDF_CONFIG.BANNER_WIDTH, 
      alignment: 'center', 
      margin: [0, 0, 0, 10] 
    });
  } catch (error) {
    console.warn('Failed to load banner image for PDF:', error);
    // Fallback to text header
    content.push({
      text: 'FIELD TRAINING PROGRAM',
      style: 'bannerText',
      alignment: 'center',
      margin: [0, 0, 0, 10]
    });
  }

  /* Report number at top right */
  content.push({
    text: `Report Number: ${report.reportId || 'N/A'}`,
    style: 'reportNumber',
    alignment: 'right',
    margin: [0, 0, 0, 5],
  });

  /* Title block with enhanced styling */
  content.push(
    { text: 'End of Week Report', style: 'header' },
    { 
      canvas: [
        {
          type: 'line',
          x1: 0, y1: 0,
          x2: 515, y2: 0,
          lineWidth: 2,
          lineColor: PDF_CONFIG.COLORS.secondary,
        }
      ],
      margin: [0, 0, 0, 10]
    },
    { 
      text: `Week Ending: ${formatPdfDate(report.weekEndingDate)}`, 
      style: 'subheader',
      bold: true,
      fontSize: 12,
      color: PDF_CONFIG.COLORS.primary,
    },
    {
      table: {
        widths: ['50%', '50%'],
        body: [
          [
            { 
              text: `Probationary Officer: ${report.traineeName || 'Unknown'} ${report.traineeIdNumber ? `(#${report.traineeIdNumber})` : ''}`, 
              style: 'subheader',
              border: [false, false, false, false],
            },
            { 
              text: `FTO: ${report.trainerName || 'Unknown'} ${report.trainerIdNumber ? `(#${report.trainerIdNumber})` : ''}`, 
              style: 'subheader', 
              alignment: 'right',
              border: [false, false, false, false],
            },
          ]
        ]
      },
      layout: 'noBorders',
      margin: [0, 5, 0, 20],
    }
  );

  /* Performance table with improved design */
  if (tableData.length) {
    // Limit columns if too many
    let displayDates = sortedDorDates;
    if (displayDates.length > PDF_CONFIG.MAX_DATE_COLUMNS) {
      displayDates = displayDates.slice(-PDF_CONFIG.MAX_DATE_COLUMNS);
      content.push({
        text: `Note: Showing last ${PDF_CONFIG.MAX_DATE_COLUMNS} evaluations. Full history available in system.`,
        style: 'footnote',
        margin: [0, 0, 0, 5]
      });
    }

    const hdr = displayDates.map(d => ({
      date: d,
      label: format(parseISO(`${d}T12:00:00Z`), 'MM/dd'),
    }));

    // Calculate dynamic column widths
    const dateColumnWidth = Math.max(35, Math.min(50, 300 / hdr.length));
    const widths = ['*', ...hdr.map(() => dateColumnWidth), 45];

    const headerRow = [
      { text: 'Rating Category', style: 'tableHeader' },
      ...hdr.map(({ date, label }) => {
        const tId = tableData[0]?.trainerIds[date];
        return {
          stack: [
            { text: label, style: 'tableHeader', alignment: 'center' },
            { text: tId ? `#${tId}` : '', style: 'tableHeaderSmall', alignment: 'center' },
          ],
          fillColor: PDF_CONFIG.COLORS.tableHeader,
          margin: [0, 2, 0, 2],
        };
      }),
      { text: 'Avg', style: 'tableHeader', alignment: 'center' },
    ];

    const body = [headerRow];
    let currentCategory = '';
    
    tableData.forEach((q, index) => {
      // Add category separator
      if (q.categoryLabel !== currentCategory) {
        currentCategory = q.categoryLabel;
        body.push([
          {
            text: currentCategory,
            colSpan: widths.length,
            style: {
              fontSize: 10,
              bold: true,
              color: PDF_CONFIG.COLORS.primary,
              fillColor: PDF_CONFIG.COLORS.headerBg,
              margin: [4, 4],
            },
          },
          ...Array(widths.length - 1).fill({ text: '' })
        ]);
      }

      const avgRating = calculateAverage(q.ratings);
      const avgStyle = getRatingStyle(avgRating === 'N/A' ? null : Number(avgRating));

      body.push([
        { text: q.question, style: 'tableCell' },
        ...hdr.map(({ date }) => {
          const rating = q.ratings[date];
          return { 
            text: rating !== null ? rating.toString() : '—', 
            alignment: 'center', 
            style: getRatingStyle(rating),
          };
        }),
        { text: avgRating, alignment: 'center', style: avgStyle },
      ]);
    });

    content.push(
      { text: 'Performance Evaluation', style: 'sectionHeader' },
      {
        margin: [0, 0, 0, 8],
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: 'Rating Scale: 1 = Not Acceptable | 2 = Needs Improvement | 3 = Acceptable | 4 = Above Average | 5 = Superior',
                fontSize: 8,
                italics: true,
                color: PDF_CONFIG.COLORS.text,
                fillColor: PDF_CONFIG.COLORS.headerBg,
                alignment: 'center',
                margin: [10, 4, 10, 4],
                border: [false, false, false, false],
              }
            ]
          ]
        },
        layout: 'noBorders',
      },
      {
        table: { 
          headerRows: 1, 
          widths, 
          body,
          dontBreakRows: true,
        },
        layout: {
          fillColor: (i, node) => {
            if (i === 0) return null; // Header has its own fill
            if (node && node.table && node.table.body && node.table.body[i]) {
              const rowData = node.table.body[i];
              if (rowData[0] && rowData[0].colSpan) return null; // Category row has its own fill
            }
            return i % 2 === 0 ? PDF_CONFIG.COLORS.tableAlt : null;
          },
          hLineWidth: (i) => i === 1 ? 1 : 0,
          hLineColor: () => PDF_CONFIG.COLORS.border,
          vLineWidth: () => 0,
          paddingLeft: () => PDF_CONFIG.TABLE_PADDING.left,
          paddingRight: () => PDF_CONFIG.TABLE_PADDING.right,
          paddingTop: () => PDF_CONFIG.TABLE_PADDING.top,
          paddingBottom: () => PDF_CONFIG.TABLE_PADDING.bottom,
        },
      },
    );
  }

  /* Explanations table with better layout */
  if (explanations.length) {
    content.push(
      { text: 'Explanations for Ratings of 1 or 5', style: 'sectionHeader' },
      {
        table: {
          headerRows: 1,
          widths: ['12%', '25%', '8%', '*', '12%'],
          body: [
            [
              { text: 'Date', style: 'tableHeader', alignment: 'center' },
              { text: 'Question', style: 'tableHeader' },
              { text: 'Rating', style: 'tableHeader', alignment: 'center' },
              { text: 'Explanation', style: 'tableHeader' },
              { text: 'FTO', style: 'tableHeader', alignment: 'center' },
            ],
            ...explanations.map((e, index) => [
              { text: e.date, style: 'tableCell', alignment: 'center' },
              { text: e.question, style: 'tableCell' },
              { 
                text: e.rating, 
                style: getRatingStyle(e.rating),
                alignment: 'center',
                bold: true,
              },
              { text: e.explanation, style: 'tableCell' },
              { text: e.trainerIdNumber ? `#${e.trainerIdNumber}` : 'N/A', style: 'tableCell', alignment: 'center' },
            ]),
          ],
        },
        layout: {
          fillColor: (i) => {
            if (i === 0) return null;
            return i % 2 === 0 ? PDF_CONFIG.COLORS.tableAlt : null;
          },
          hLineWidth: (i) => i === 1 ? 1 : 0,
          hLineColor: () => PDF_CONFIG.COLORS.border,
          vLineWidth: () => 0,
          paddingLeft: () => PDF_CONFIG.TABLE_PADDING.left,
          paddingRight: () => PDF_CONFIG.TABLE_PADDING.right,
          paddingTop: () => PDF_CONFIG.TABLE_PADDING.top,
          paddingBottom: () => PDF_CONFIG.TABLE_PADDING.bottom,
        },
      },
    );
  }

  /* Page break for narrative sections if needed */
  if (content.length > 20) {
    content.push({ text: '', pageBreak: 'before' });
  }

  /* Narrative sections with enhanced styling */
  const addSection = (title, text) => {
    const sectionContent = text || `No ${title.toLowerCase()} provided.`;
    
    content.push(
      {
        text: title,
        style: 'sectionHeader',
      },
      {
        margin: [0, 0, 0, 10],
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: sectionContent,
                style: 'content',
                fillColor: 'white',
                border: [true, true, true, true],
                borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                margin: [10, 10, 10, 10],
              }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => PDF_CONFIG.COLORS.border,
          vLineColor: () => PDF_CONFIG.COLORS.border,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
          fillColor: () => null,
        }
      },
    );
  };

  addSection('Week Summary', report.weekSummary);
  addSection('Positive Observations', report.positiveObservations);
  addSection('Negative Observations', report.negativeObservations);
  addSection('Recommendations', report.recommendations);
  addSection('Goals for Next Week', report.goals);

  /* Enhanced signature block */
  content.push({ text: '', margin: [0, 0, 0, 40] });
  content.push({
    columns: [
      {
        width: '48%',
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 200, y2: 0,
                lineWidth: 1,
                lineColor: PDF_CONFIG.COLORS.text,
                dash: { length: 1 },
              }
            ],
            margin: [0, 20, 0, 5],
          },
          { 
            text: "Probationary Officer's Signature", 
            style: 'signatureLabel',
            color: PDF_CONFIG.COLORS.lightText,
          },
          { 
            text: `${report.traineeName || 'Unknown'} ${report.traineeIdNumber ? `(#${report.traineeIdNumber})` : ''}`,
            fontSize: 9,
            color: PDF_CONFIG.COLORS.text,
            margin: [0, 2, 0, 5],
          },
          { 
            text: 'Date: _____________________', 
            style: 'signatureDate',
            color: PDF_CONFIG.COLORS.lightText,
          },
        ],
      },
      { width: '4%', text: '' },
      {
        width: '48%',
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 200, y2: 0,
                lineWidth: 1,
                lineColor: PDF_CONFIG.COLORS.text,
                dash: { length: 1 },
              }
            ],
            margin: [0, 20, 0, 5],
            alignment: 'right',
          },
          { 
            text: "Field Training Officer's Signature", 
            style: 'signatureLabel', 
            alignment: 'right',
            color: PDF_CONFIG.COLORS.lightText,
          },
          { 
            text: `${report.trainerName || 'Unknown'} ${report.trainerIdNumber ? `(#${report.trainerIdNumber})` : ''}`, 
            fontSize: 9,
            alignment: 'right',
            color: PDF_CONFIG.COLORS.text,
            margin: [0, 2, 0, 5],
          },
          { 
            text: 'Date: _____________________', 
            style: 'signatureDate', 
            alignment: 'right',
            color: PDF_CONFIG.COLORS.lightText,
          },
        ],
      },
    ],
  });

  /* Footer */
  content.push({
    text: `Generated on ${format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}`,
    style: 'footerText',
    alignment: 'center',
    margin: [0, 20, 0, 0],
    fontSize: 7,
    color: PDF_CONFIG.COLORS.lightText,
  });

  return content;
};

/* ------------------------------------------------ performance data helper ---- */
const performanceQuestionsList = [
  { id: 'A1', categoryLabel: 'A. Appearance', question: 'General appearance, weight, uniform, hygiene, hair, etc.' },
  { id: 'B1', categoryLabel: 'B. Job Ready', question: 'Forms/Equipment' },
  { id: 'C1', categoryLabel: 'C. Attitude', question: 'Acceptance of constructive criticism - verbal behavior' },
  { id: 'C2', categoryLabel: 'C. Attitude', question: 'Attitude toward police service' },
  { id: 'C3', categoryLabel: 'C. Attitude', question: 'Responds promptly to calls' },
  { id: 'D1', categoryLabel: 'D. Knowledge', question: 'Driving Skills - Normal' },
  { id: 'D2', categoryLabel: 'D. Knowledge', question: 'High speed, stress conditions' },
  { id: 'D3', categoryLabel: 'D. Knowledge', question: 'Knowledge of the town and wards' },
  { id: 'D4', categoryLabel: 'D. Knowledge', question: 'Report writing organization, completeness, grammar, and spelling' },
  { id: 'D5', categoryLabel: 'D. Knowledge', question: 'Appropriate use of time relevant to investigation and report writing' },
  { id: 'D6', categoryLabel: 'D. Knowledge', question: 'Accident investigation to include collection of physical evidence' },
  { id: 'D7', categoryLabel: 'D. Knowledge', question: 'Self-motivation/self-initiated police action' },
  { id: 'D8', categoryLabel: 'D. Knowledge', question: 'Proper use of the police radio, etiquette, codes, and signals' },
  { id: 'F1', categoryLabel: 'F. Relationships', question: 'With Citizens' },
  { id: 'F2', categoryLabel: 'F. Relationships', question: 'Cross-cultural' },
  { id: 'F3', categoryLabel: 'F. Relationships', question: 'With FTO' },
  { id: 'F4', categoryLabel: 'F. Relationships', question: 'With squad members' },
  { id: 'F5', categoryLabel: 'F. Relationships', question: 'With supervisors' },
  { id: 'G1', categoryLabel: 'G. Dependability', question: 'Report to work on time' },
  { id: 'G2', categoryLabel: 'G. Dependability', question: 'Use of sick leave' },
  { id: 'H1', categoryLabel: 'H. Interview/Interrogation Skills', question: 'Ability to use proper questioning techniques' },
  { id: 'J1', categoryLabel: 'J. Control of Conflict', question: 'Use of appropriate levels of force' },
];

export const preparePerformanceData = async (eow, traineeId) => {
  if (!eow.weekEndingDate || !traineeId) return { tableData: [], sortedDorDates: [], explanations: [] };

  const eowDate = parseDate(eow.weekEndingDate);

  /* find previous EOWR to set start date */
  const prevQuery = query(
    collection(db, 'endOfWeekReports'),
    where('traineeId', '==', traineeId),
    where('weekEndingDate', '<', Timestamp.fromDate(eowDate)),
    orderBy('weekEndingDate', 'desc'),
    limit(1)
  );
  const prevSnap = await getDocs(prevQuery);
  const startDate = prevSnap.empty
    ? new Date(0)
    : startOfDay(addDays(prevSnap.docs[0].data().weekEndingDate.toDate(), 1));
  const endDate = endOfDay(eowDate);

  /* pull DORs */
  const dorsSnap = await getDocs(query(
    collection(db, 'dors'),
    where('traineeId', '==', traineeId),
    where('date', '>=', Timestamp.fromDate(startDate)),
    where('date', '<=', Timestamp.fromDate(endDate)),
    orderBy('date', 'asc'),
  ));
  let dors = dorsSnap.docs.map(s => ({ ...s.data(), date: parseDate(s.data().date) }));
  dors.sort((a, b) => a.date - b.date);
  if (dors.length > 5) dors = dors.slice(-5);

  const sortedDorDates = dors.map(d => format(d.date, 'yyyy-MM-dd'))
                             .filter((d, i, a) => a.indexOf(d) === i)
                             .sort();

  /* build rating table */
  const tableData = performanceQuestionsList.map(q => {
    const ratings = {};
    const trainerIds = {};
    sortedDorDates.forEach(date => {
      const dor = dors.find(d => format(d.date, 'yyyy-MM-dd') === date);
      if (dor?.performanceQuestions) {
        const pq = dor.performanceQuestions.find(p => p.id === q.id);
        ratings[date] = pq ? Number(pq.rating) : null;
        trainerIds[date] = dor.trainerIdNumber || 'N/A';
      } else {
        ratings[date] = null;
        trainerIds[date] = null;
      }
    });
    return { ...q, ratings, trainerIds };
  });

  /* explanations list */
  const explanations = [];
  dors.forEach(dor => {
    dor.performanceQuestions?.forEach(pq => {
      if ((pq.rating === '1' || pq.rating === '5') && pq.explanation) {
        explanations.push({
          date: format(dor.date, 'MM/dd/yyyy'),
          question: pq.question,
          rating: pq.rating,
          explanation: pq.explanation,
          trainerIdNumber: dor.trainerIdNumber || 'N/A',
        });
      }
    });
  });

  return { tableData, sortedDorDates, explanations };
};

/* ------------------------------------------------ utility ---------- */
const fmtDate = d => isValid(parseDate(d)) ? format(parseDate(d), 'MMMM d, yyyy') : 'N/A';
const avg = r => {
  const arr = Object.values(r).filter(x => x !== null).map(Number);
  return arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2) : 'N/A';
};

/* small JSX helper for narrative cards */
const Narrative = ({ icon, title, text, card, head }) => (
  <section className={`${card} p-4 sm:p-6 mt-6`}>
    <h2 className={head}>{icon}{title}</h2>
    <p className="mt-3 text-xs sm:text-sm">{text}</p>
  </section>
);

/* ------------------------------------------------ Loading Skeleton ---- */
const EOWRDetailSkeleton = () => (
  <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark p-3 sm:p-6">
    <div className="max-w-3xl mx-auto space-y-6 animate-pulse">
      {/* PDF Button Skeleton */}
      <div className="hidden lg:flex justify-end mb-4 lg:mb-6">
        <div className="h-11 w-24 bg-secondary-light dark:bg-secondary-dark rounded-md"></div>
      </div>

      {/* Header Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="h-6 w-56 bg-secondary-light dark:bg-secondary-dark rounded mb-3"></div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-4 h-4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-4 w-28 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-4 w-32 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Table Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-48 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-3">
          {/* Table header */}
          <div className="grid grid-cols-6 gap-2">
            <div className="h-8 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            ))}
          </div>
          {/* Table rows */}
          {[1, 2, 3, 4, 5].map((row) => (
            <div key={row} className="grid grid-cols-6 gap-2">
              <div className="h-6 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded"></div>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-6 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded"></div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Explanations Table Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-40 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((row) => (
            <div key={row} className="grid grid-cols-5 gap-2">
              <div className="h-6 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded"></div>
              <div className="h-6 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded"></div>
              <div className="h-6 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded"></div>
              <div className="h-6 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded col-span-2"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Narrative Cards Skeleton */}
      {[1, 2, 3, 4, 5].map((card) => (
        <div key={card} className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            <div className="h-6 w-36 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          </div>
          <div className="space-y-2">
            <div className="h-3 w-full bg-secondary-light dark:bg-secondary-dark rounded"></div>
            <div className="h-3 w-4/5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            <div className="h-3 w-3/4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          </div>
        </div>
      ))}

      {/* Report ID Skeleton */}
      <div className="h-3 w-24 bg-secondary-light dark:bg-secondary-dark rounded ml-auto"></div>
    </div>
  </div>
);

/* ------------------------------------------------ React page ---- */
export default function EndOfWeekReportDetailPage() {
  const { reportId } = useParams();
  const navigate     = useNavigate();
  const { currentUser, role } = useContext(AuthContext);

  const [report, setReport]   = useState(null);
  const [perf, setPerf]       = useState({ tableData: [], sortedDorDates: [], explanations: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      if (!currentUser) { navigate('/login'); return; }
      try {
        const snap = await getDoc(doc(db, 'endOfWeekReports', reportId));
        if (!snap.exists()) { toast.error('Report not found'); navigate('/'); return; }

        const data = snap.data();
        const processedReport = {
          ...data,
          weekEndingDate: parseDate(data.weekEndingDate),
          traineeName:    data.traineeName   || 'Unknown',
          trainerName:    data.trainerName   || 'Unknown',
          traineeIdNumber:data.traineeIdNumber || '',
          trainerIdNumber:data.trainerIdNumber || '',
        };
        setReport(processedReport);
        
        // Validate data before processing
        if (data.traineeId) {
          const performanceData = await preparePerformanceData(data, data.traineeId);
          setPerf(performanceData);
        } else {
          console.warn('No traineeId found in report data');
        }
      } catch (e) { 
        console.error('Error loading report:', e); 
        toast.error('Failed to load report details'); 
      }
      finally { setLoading(false); }
    };
    run();
  }, [reportId, currentUser, navigate]);

  /* Memoize PDF content to avoid regeneration */
  const pdfContentPromise = useMemo(() => {
    if (!report || !perf) return null;
    return getEOWRPdfContentDefinition(report, perf);
  }, [report, perf]);

  /* Enhanced PDF download with metadata */
  const downloadPdf = async () => {
    if (!report || !pdfContentPromise) return;
    
    const loadingToast = toast.loading('Generating PDF...');
    
    try {
      const content = await pdfContentPromise;
      const docDefinition = {
        pageMargins: PDF_CONFIG.PAGE_MARGINS,
        content,
        styles: enhancedPdfStyles,
        info: {
          title: `End of Week Report - ${report.traineeName}`,
          author: report.trainerName,
          subject: `Week ending ${formatPdfDate(report.weekEndingDate, 'yyyy-MM-dd')}`,
          keywords: 'field training, weekly report, performance evaluation, FTO, trainee',
          creator: 'Field Training Management System',
          producer: 'Field Training Management System',
          creationDate: new Date(),
        },
        // Page settings
        pageSize: 'LETTER',
        pageOrientation: 'portrait',
        // Header for pages 2+
        header: (currentPage, pageCount) => {
          if (currentPage === 1) return null;
          return {
            columns: [
              {
                text: `End of Week Report - ${report.traineeName}`,
                style: 'footerText',
                alignment: 'left',
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerText',
                alignment: 'right',
              }
            ],
            margin: [40, 10, 40, 0],
          };
        },
      };
      
      pdfMake.createPdf(docDefinition)
             .download(`EndOfWeekReport_${report.reportId || reportId}_${format(report.weekEndingDate, 'yyyy-MM-dd')}.pdf`);
      
      toast.update(loadingToast, { 
        render: 'PDF generated successfully!', 
        type: 'success', 
        isLoading: false,
        autoClose: 3000 
      });
    } catch (e) { 
      console.error('PDF generation error:', e); 
      toast.update(loadingToast, { 
        render: ERRORS.GENERATE_PDF_FAILED || 'Failed to generate PDF', 
        type: 'error', 
        isLoading: false,
        autoClose: 5000 
      });
    }
  };

  /* tailwind snippets */
  const card   = 'rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark';
  const head   = 'font-semibold flex items-center gap-2 text-base sm:text-lg';
  const label  = 'font-semibold text-[11px] sm:text-xs';
  const button = 'min-h-[44px] px-4 py-2 rounded-md focus-visible:ring-2 focus-visible:ring-blue-500';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark p-3 sm:p-6"
    >
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
        className="max-w-3xl mx-auto"
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <EOWRDetailSkeleton />
            </motion.div>
          ) : !report ? (
            <motion.div
              key="not-found"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center min-h-screen"
            >
              <div className={`${card} p-8 text-center`}>
                <FiCalendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h2 className="text-xl font-semibold mb-2">Report Not Found</h2>
                <p className="text-gray-600 mb-4">The End of Week Report you're looking for doesn't exist.</p>
                <button 
                  onClick={() => navigate('/')}
                  className={`${button} bg-blue-600 hover:bg-blue-700 text-white`}
                >
                  Go Back Home
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {/* desktop PDF button */}
              {(role === 'admin' || role === 'trainer') && (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="hidden lg:flex justify-end mb-4 lg:mb-6"
                >
                  <button onClick={downloadPdf} className={`${button} bg-blue-600 hover:bg-blue-700 text-white shadow flex items-center gap-2`}>
                    <FiDownload /> PDF
                  </button>
                </motion.div>
              )}

              {/* header */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className={`${card} p-4 sm:p-6`}
              >
                <h1 className="text-lg sm:text-xl font-bold mb-3 flex items-center gap-2"><FiCalendar /> End of Week Report</h1>
                <div className="space-y-1 text-sm">
                  <p className="flex items-center gap-2"><FiCalendar /><span className={label}>Week Ending:</span>{fmtDate(report.weekEndingDate)}</p>
                  <p className="flex items-center gap-2"><FiUser /><span className={label}>Probationary Officer:</span>{report.traineeName} {report.traineeIdNumber}</p>
                  <p className="flex items-center gap-2"><FiUser /><span className={label}>FTO:</span>{report.trainerName} {report.trainerIdNumber}</p>
                </div>
              </motion.div>

              {/* performance table */}
              {perf.tableData.length > 0 && (
                <motion.section 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  className={`${card} p-4 sm:p-6 mt-6`}
                >
                  <h2 className={head}><FiClipboard /> Performance Evaluation</h2>
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full text-[9px] xs:text-[10px] sm:text-xs divide-y divide-secondary-light dark:divide-secondary-dark">
                      <thead className="bg-secondary-light dark:bg-secondary-dark">
                        <tr>
                          <th className="px-1 sm:px-2 py-1 sm:py-2 text-left">Category</th>
                          {perf.sortedDorDates.map(d => {
                            const tId = perf.tableData[0]?.trainerIds[d];
                            return (
                              <th key={d} className="px-1 sm:px-2 py-1 sm:py-2 text-center">
                                {format(parseISO(`${d}T12:00:00Z`), 'MM/dd')}<br />
                                <span className="opacity-70 text-[8px] sm:text-[10px]">#{tId || '—'}</span>
                              </th>
                            );
                          })}
                          <th className="px-1 sm:px-2 py-1 sm:py-2 text-center">Avg</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-light dark:divide-secondary-dark">
                        {perf.tableData.map((q, i) => (
                          <motion.tr 
                            key={q.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: 0.6 + (i * 0.05) }}
                            className={i % 2 === 0 ? 'bg-secondary-light/5 dark:bg-secondary-dark/5' : ''}
                          >
                            <td className="px-1 sm:px-2 py-1 sm:py-2">{q.question}</td>
                            {perf.sortedDorDates.map(d => {
                              const r = q.ratings[d];
                              return (
                                <td key={d} className="px-1 sm:px-2 py-1 sm:py-2 text-center">
                                  {r !== null ? (r === 1
                                    ? <span className="inline-flex items-center justify-center border border-red-500 rounded-full w-3 h-3 sm:w-4 sm:h-4">{r}</span>
                                    : r)
                                    : '—'}
                                </td>
                              );
                            })}
                            <td className="px-1 sm:px-2 py-1 sm:py-2 text-center">{avg(q.ratings)}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.section>
              )}

              {/* explanations */}
              {perf.explanations.length > 0 && (
                <motion.section 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                  className={`${card} p-4 sm:p-6 mt-6`}
                >
                  <h2 className={head}><FiInfo /> Explanations for 1 or 5</h2>
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full text-[9px] xs:text-[10px] sm:text-xs divide-y divide-secondary-light dark:divide-secondary-dark">
                      <thead className="bg-secondary-light dark:bg-secondary-dark">
                        <tr>
                          <th className="px-1 sm:px-2 py-1 sm:py-2 text-left">Date</th>
                          <th className="px-1 sm:px-2 py-1 sm:py-2 text-left">Question</th>
                          <th className="px-1 sm:px-2 py-1 sm:py-2 text-center">Rating</th>
                          <th className="px-1 sm:px-2 py-1 sm:py-2 text-left">Explanation</th>
                          <th className="px-1 sm:px-2 py-1 sm:py-2 text-center">FTO ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-light dark:divide-secondary-dark">
                        {perf.explanations.map((e, i) => (
                          <motion.tr 
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: 0.7 + (i * 0.1) }}
                            className={i % 2 === 0 ? 'bg-secondary-light/5 dark:bg-secondary-dark/5' : ''}
                          >
                            <td className="px-1 sm:px-2 py-1 sm:py-2">{e.date}</td>
                            <td className="px-1 sm:px-2 py-1 sm:py-2">{e.question}</td>
                            <td className="px-1 sm:px-2 py-1 sm:py-2 text-center">{e.rating}</td>
                            <td className="px-1 sm:px-2 py-1 sm:py-2">{e.explanation}</td>
                            <td className="px-1 sm:px-2 py-1 sm:py-2 text-center">{e.trainerIdNumber}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.section>
              )}

              {/* narrative cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
              >
                <Narrative icon={<FiClipboard />} title="Week Summary"         text={report.weekSummary || 'None provided.'}  card={card} head={head} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.8 }}
              >
                <Narrative icon={<FiThumbsUp />}  title="Positive Observations" text={report.positiveObservations || 'None provided.'} card={card} head={head} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.9 }}
              >
                <Narrative icon={<FiThumbsDown />}title="Negative Observations" text={report.negativeObservations || 'None provided.'} card={card} head={head} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 1.0 }}
              >
                <Narrative icon={<FiKey />}       title="Recommendations"       text={report.recommendations || 'None provided.'} card={card} head={head} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 1.1 }}
              >
                <Narrative icon={<FiTarget />}    title="Goals for Next Week"   text={report.goals || 'None provided.'} card={card} head={head} />
              </motion.div>

              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 1.2 }}
                className="mt-6 text-[11px] text-right pr-20 text-text-light/60 dark:text-text-dark/60"
              >
                Report #: {report.reportId}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* mobile FAB */}
        {(role === 'admin' || role === 'trainer') && !loading && report && (
          <motion.button 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            onClick={downloadPdf}
            className="lg:hidden fixed bottom-28 right-4 z-[1300] bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-xl p-4 focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <FiDownload className="w-5 h-5" />
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
}

// Export the enhanced styles for use in compiled PDFs
export { enhancedPdfStyles };
